import { Subject } from 'rxjs';

import { API_URL, getApiUrl } from '@/utils/api';
import { getMainLocale } from '@/utils/locales';
import { AppFullLocale } from '@/types';

import ApiBase from './ApiBase';
import { AppDispatch } from '@/redux';
import { setPaymentInfosAre, setPaymentStatusInsideCheckout } from '@/redux/slices/checkout.slice';
import { updateCartAttributeByName } from '@/redux/thunks/checkout/updateCartAttributeByName';
import { CartModel, CartPayModeModel } from '@/models';
import { ProviderInterface } from '@/services/ProviderInterface';
import { LoggerService } from '@/services/Logger.service';
import { sendLogs } from '@/redux/thunks/shared/send-logs';
import { LOG_MSG, LOG_URL } from '@/constants/logger';
import { URLS } from '@/constants/urls';

// eslint-disable-next-line no-var
declare var HiPay: any;

export default class HipayService extends ApiBase implements ProviderInterface {
  static baseUrl = getApiUrl(API_URL.ADMIN, '/payment');
  static userCartUrl = getApiUrl(API_URL.USER, '/cart');

  private hipayInstance = undefined;
  private cardInstance = undefined;
  private dispatch: AppDispatch;
  private readonly paymentErrorCodes = [
    '3000001', '3000002', '3010002', '3010004', '3010006', '3010007', '3020001',
    '4000001', '4000002', '4000003', '4000007', '4010202', '4010203', '4010204', '4010205', '4010206', '4010207',
  ];

  constructor(api) {
    super(api);
  }

  async load(): Promise<void> {
    this.cleanPreviousLoads();
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = URLS.HIPAY_SDK;
      document.body.appendChild(script);
      script.onload = () => resolve();
    });
  }

  /**
   * Initialize the HiPay core
   *
   * @param {*} locale The locale of the current site
   * @param {*} username The public username to instantiate hipay form
   * @param {*} password The public password to instantiate hipay form
   */
  async initPaymentProvider({ locale, username, password } : {locale: AppFullLocale, username: string, password: string}) {
    if (this.hipayInstance === undefined) {
      const HIPAY_ENVIRONMENT = process.env.NEXT_PUBLIC_HIPAY_ENVIRONMENT || 'stage';
      const lang = getMainLocale(locale);
      // eslint-disable-next-line no-undef, new-cap
      this.hipayInstance = HiPay({
        username: username,
        password: password,
        environment: HIPAY_ENVIRONMENT,
        lang
      });
    }
    return true;
  }

  async createPaymentProviderForm(dispatch: AppDispatch, options: unknown, paymentMode: CartPayModeModel) {
    if (this.cardInstance) {
      this.cardInstance.destroy();
    }
    this.dispatch = dispatch;

    try {
      this.cardInstance = this.hipayInstance.create('card', this.optionsToHiPay(options));
      this.cardInstance.on('change', async (response: any) => {
        if (response.valid) {
          try {
            const paymentData = await this.cardInstance.getPaymentData();
            const hipayCardInfos = this.mapHipayCreditCardInfo(paymentData);
            dispatch(setPaymentInfosAre('valid'));
            await dispatch(updateCartAttributeByName(
              {
                attributeName: 'browserInfo',
                newValue: this.hipayInstance.getBrowserInfo()
              }
            ));
            await dispatch(updateCartAttributeByName(
              {
                attributeName: 'paiementMode',
                newValue: { ...paymentMode, cardInformation: this.mapHipayCreditCardInfo(hipayCardInfos) }
              }
            ));
            return paymentData;
          } catch (error) {
            dispatch(setPaymentInfosAre('invalid'));
          }
        } else {
          dispatch(setPaymentInfosAre('invalid'));
        }
      });
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Update a credit card token with cvc
   *
   * @param {*} params Parameters for update the token
   * @return {Observable} An observable object
   */
  updateSaveCreditCardCvc(params) {
    const updateCvcSubject = new Subject();

    try {
      if (this.hipayInstance) {
        this.hipayInstance.updateToken(params).then(
          (response) => {
            updateCvcSubject.next(response);
          },
          (error) => {
            updateCvcSubject.error(error);
          }
        );
      }
    } catch (error) {
      updateCvcSubject.error(error);
    }

    return updateCvcSubject;
  }

  optionsToHiPay(options) {
    return {
      fields: {
        cardHolder: {
          selector: options.cardHolder.selector,
          placeholder: options.cardHolder.placeholder
        },
        cardNumber: {
          selector: options.cardNumber.selector,
          placeholder: options.cardNumber.placeholder
        },
        expiryDate: {
          selector: options.expiryDate.selector
        },
        cvc: {
          selector: options.cvc,
          helpSelector: `${options.cvc}-help`
        }
      },
      styles: {
        base: {
          fontSize: '16px'
        }
      }
    };
  }

  mapHipayCreditCardInfo(creditCardInfo) {
    return creditCardInfo ? {
      brand: creditCardInfo.brand,
      paymentProduct: creditCardInfo.payment_product || creditCardInfo.paymentProduct,
      cardExpireMonth: creditCardInfo.card_expiry_month,
      cardExpireYear: creditCardInfo.card_expiry_year,
      cardHolder: creditCardInfo.card_holder,
      cardType: creditCardInfo.card_type,
      country: creditCardInfo.country,
      forbidden_issuer_country: creditCardInfo.forbidden_issuer_country,
      pan: creditCardInfo.pan,
      token: creditCardInfo.token
    } : undefined;
  }

  async processPayment(cart: CartModel) {
    try {
      const fingerprint = await this.getFingerprint();
      const response = await this.api.putPromisedJSON(
        `${HipayService.userCartUrl}/${cart.storeId}/processOrder`,
        fingerprint ? { ...cart, paiementMode: { ...cart.paiementMode, fingerprint: fingerprint } } : cart
      );

      return this.handlePaymentComplete(response);
    } catch (error) {
      LoggerService.sendLog(cart.id, error.error, cart.storeId, 'hipay V2 process payment');
      if (error.error?.reason?.code && this.paymentErrorCodes.includes(error.error.reason.code)) {
        throw new Error(`errors.hipay.${error.error.reason.code}`);
      }
      throw new Error('errors.genericErrorMessage.main');
    }
  }

  async validatePayment(cart: CartModel) {
    try {
      const response = await this.api.putPromisedJSON(`${HipayService.userCartUrl}/${cart.storeId}/V2/validateOrder`, cart);
      this.dispatch(sendLogs({ message: LOG_MSG.HIPAY.SUCCESS, url: LOG_URL.HIPAY, data: response }));
      return response;
    } catch (error) {
      LoggerService.sendLog(cart.id, error.error, cart.storeId, 'hipay V2 validate Order');
      throw error;
    }
  }

  getBrowserInfo() {
    return this.hipayInstance.getBrowserInfo();
  }

  getFingerprint() {
    return this.hipayInstance && this.hipayInstance.getDeviceFingerprint();
  }

  handlePaymentComplete(response: Record<string, unknown>) {
    const status = response.state as 'forwarding' | 'pending' | 'completed' | 'declined' | 'error';

    switch (status) {
      case 'forwarding':
        this.dispatch(setPaymentStatusInsideCheckout({ status: 'forwarding', redirectUrl: response.forwardUrl as string }));
        break;
      case 'completed':
      case 'pending':
        this.dispatch(setPaymentStatusInsideCheckout({ status: 'authorized', redirectUrl: '' as string }));
        break;
      default:
        if (this.hasCode(response.reason)) {
          throw new Error(`HipayService: handlePaymentComplete: Unknown status ${status}`, { cause: { code: response.reason.code as string } });
        }
    }
  }

  hasCode(reason: unknown): reason is { code: string } {
    return typeof reason === 'object' && reason !== null && 'code' in reason;
  }

  private cleanPreviousLoads(): void {
    const existingHostedFields = Array
      .from(document.querySelectorAll(`iframe[src^="${URLS.HIPAY_HOSTED_FIELDS}"]`));
    if (existingHostedFields.length > 0) {
      existingHostedFields.forEach((iframe) => iframe.remove());
    }
    const existingScript = document.querySelector(`script[src="${URLS.HIPAY_SDK}"]`);
    if (existingScript) {
      existingScript.remove();
    }
  }

  set _dispatch(_dispatch: AppDispatch) {
    this.dispatch = _dispatch;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  set _storeId(_storeId: string) {}
}
